import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useParams } from 'react-router-dom';
import {
    getCaseType,
    getSubCaseType,
    TreatmentApproach,
    TreatmentDetails
} from '../../pages/patient/CreationProcess/treatmentDetails';
import { loadPatientCreation, PatientCreationData } from '../../services/patient/creation';
import { formatUSPhoneNumber } from '../../utils/monday';

export interface FileType {
    id: string;
    file: File;
    preview: string;
    type: string;
    name: string;
}

export interface SubmissionFormData {
    id?: string;
    practiceId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    phoneCountry?: string;
    dateOfBirth?: string;
    gender?: string;
    suggestedFees?: string;
    treatmentDuration?: string;

    caseType?: TreatmentDetails;
    subCaseType?: TreatmentApproach;

    profilePicture?: FileType;
    patientPhotos?: FileType[];
    photosDate?: string;
    patientStls?: FileType[];
    patientIteroScanNumber?: string;

    additionalInfo?: string;
    manufacturer?: 'Invisalign Aligners' | 'Spark Aligners' | 'Select';
    xrayFiles?: FileType[];
}

interface SubmissionFormContextType {
    formData: SubmissionFormData | null;
    isLoading: boolean;
    error: string | null;
    initNewSubmissionForm: () => void;
    updateSubmissionForm: (data: Partial<SubmissionFormData>) => void;
    resetSubmissionForm: () => void;
    refreshFormFields: (fields: (keyof SubmissionFormData)[]) => Promise<void>;
}

function toFormData(data: PatientCreationData): Partial<SubmissionFormData> {
    return {
        id: data.id,
        practiceId: data.practiceId ?? undefined,
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        email: data.email ?? undefined,
        phone: formatUSPhoneNumber(data.phone as string) ?? undefined,
        gender: data.gender ?? undefined,
        dateOfBirth: data.dateOfBirth ?? undefined,
        caseType: getCaseType(data.caseType as string) ?? undefined,
        subCaseType:
            getSubCaseType(data.caseType as string, data.subCaseType as string) ?? undefined,
        manufacturer: (data.manufacturer as SubmissionFormData['manufacturer']) ?? undefined,
        additionalInfo: data.additionalInfo ?? undefined,
        treatmentDuration: data.treatmentDuration ?? undefined,
        photosDate: data.photosDate ?? undefined,
        patientIteroScanNumber: data.patientIteroScanNumber ?? undefined
    };
}

async function fetchFormData(patientId: string): Promise<SubmissionFormData> {
    const creation = await loadPatientCreation(patientId);
    return toFormData(creation);
}

const SubmissionFormContext = createContext<SubmissionFormContextType | undefined>(undefined);

interface SubmissionFormProviderProps {
    children: ReactNode;
}

export function SubmissionFormProvider({ children }: SubmissionFormProviderProps) {
    const { id: patientId } = useParams<{ id: string }>();
    const [formData, setFormData] = useState<SubmissionFormData | null>(null);
    // Start in the loading state when editing an existing patient so the layout
    // spinner shows on first render (the load kicks off in the effect below).
    const [isLoading, setIsLoading] = useState<boolean>(!!patientId);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!patientId) {
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchFormData(patientId)
            .then((merged) => {
                if (!cancelled) {
                    setFormData(merged);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    const message =
                        err instanceof Error ? err.message : 'Failed to load patient data';
                    setError(message);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [patientId]);

    const initNewSubmissionForm = useCallback(() => {
        setFormData({});
    }, []);

    const updateSubmissionForm = useCallback((data: Partial<SubmissionFormData>) => {
        setFormData((prev) => ({ ...(prev || {}), ...data }));
    }, []);

    const resetSubmissionForm = useCallback(() => {
        setFormData(null);
    }, []);

    const refreshFormFields = useCallback(
        async (fields: (keyof SubmissionFormData)[]) => {
            const id = formData?.id;
            if (!id) {
                return;
            }

            try {
                const merged = await fetchFormData(id);
                const updatedFields: Partial<SubmissionFormData> = {};
                fields.forEach((field) => {
                    if (field in merged) {
                        (updatedFields as any)[field] = merged[field];
                    }
                });
                setFormData((prev) => ({ ...(prev || {}), ...updatedFields }));
            } catch (err: unknown) {
                const message =
                    err instanceof Error ? err.message : 'Failed to refresh patient data';
                setError(message);
            }
        },
        [formData?.id]
    );

    const value: SubmissionFormContextType = useMemo(
        () => ({
            formData,
            isLoading,
            error,
            initNewSubmissionForm,
            updateSubmissionForm,
            resetSubmissionForm,
            refreshFormFields
        }),
        [
            formData,
            isLoading,
            error,
            initNewSubmissionForm,
            updateSubmissionForm,
            resetSubmissionForm,
            refreshFormFields
        ]
    );

    return (
        <SubmissionFormContext.Provider value={value}>{children}</SubmissionFormContext.Provider>
    );
}

export function useSubmissionForm(): SubmissionFormContextType {
    const context = useContext(SubmissionFormContext);
    if (context === undefined) {
        throw new Error('useSubmissionForm must be used within an SubmissionFormProvider');
    }
    return context;
}
